/**
 * Pure odometer-settle math for the web `RollingNumber` (task #4000).
 *
 * Extracted as a framework-free module so the upward-only guarantee can be
 * unit-tested deterministically (the rAF reel animation itself can't be driven
 * under jsdom). The mobile `RollingMoney` mirrors this exactly
 * (`artifacts/mobile/components/ui/rolling-money-math.ts`) so both surfaces
 * share one definition of "forward". Keep the two in lock-step.
 */

/**
 * Strictly-forward odometer landing.
 *
 * A digit reel is a vertical strip whose row `i` displays `i % 10`; its current
 * vertical position is `currentOffset` measured in rows (any real ≥ 0). Given a
 * target `digit` (0–9), return the nearest offset that is **>= `currentOffset`**
 * and still displays `digit`.
 *
 * Because the result is never less than `currentOffset`, the reel can only ever
 * roll FORWARD (upward, like a real car odometer that can't run backwards) to
 * settle — even when the new total is *lower* than the old one (e.g. a reel
 * showing `5` that must land on `4` rolls 5→6→7→8→9→0…→4 through the wrap, never
 * 5→4 in reverse).
 */
export function forwardSettleTarget(currentOffset: number, digit: number): number {
  // Normalise the digit into 0-9 (callers pass `Number(char)` of a /\d/ match).
  const d = ((Math.trunc(digit) % 10) + 10) % 10;
  let target = d;
  // Advance by whole rotations until we are at or past the current position.
  while (target < currentOffset) target += 10;
  return target;
}

/**
 * Shortest-path ("clicks over") odometer landing — used by the live COUNTDOWN
 * clock, not the price total.
 *
 * Given a reel currently resting at `currentOffset` (rows, where row `i` shows
 * `i % 10`), return the offset showing `digit` that is NEAREST to where we are —
 * which may be slightly BELOW the current offset. A countdown decrements one per
 * tick, so the natural motion is a single gentle step DOWN (e.g. seconds reel
 * 7→6 rolls down one row; the 0→9 borrow rolls down one row through the wrap),
 * exactly like a real odometer ticking over — instead of spinning almost a full
 * turn forward every second (the `forwardSettleTarget` behaviour, which is right
 * for an occasionally-changing price but wrong for a per-second clock).
 *
 * The result stays within ±5 rows of `currentOffset`, so the caller can keep the
 * animation inside the rendered reel window without ever going off either end.
 */
export function nearestSettleTarget(currentOffset: number, digit: number): number {
  const d = ((Math.trunc(digit) % 10) + 10) % 10;
  // The multiple-of-ten rotation of `d` closest to where the reel sits now.
  const rotations = Math.round((currentOffset - d) / 10);
  return d + rotations * 10;
}
